type FirestoreTimestampLike = {
  toMillis?: () => number;
  seconds?: number;
};

export type NotificationDateValue = (
  Date | FirestoreTimestampLike | number | string | null | undefined
);

type Place = {
  createdAt?: NotificationDateValue;
} & Record<string, unknown>;

export type PlaceNotification<TPlace extends Place> = {
  place: TPlace;
  unread: boolean;
};

const DEFAULT_NOTIFICATION_LIMIT = 20;

const finiteNumber = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

export const toTimestampMillis = (value: NotificationDateValue): number | null => {
  if (typeof value === 'number') {
    return finiteNumber(value);
  }

  if (typeof value === 'string') {
    return finiteNumber(Date.parse(value));
  }

  if (value instanceof Date) {
    return finiteNumber(value.getTime());
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  if (typeof value.toMillis === 'function') {
    try {
      const milliseconds = finiteNumber(value.toMillis());

      if (milliseconds !== null) {
        return milliseconds;
      }
    } catch {
      // Fall back to `seconds` when a timestamp-like object's method fails.
    }
  }

  const seconds = finiteNumber(value.seconds);
  return seconds === null ? null : seconds * 1000;
};

export const getPlaceNotifications = <TPlace extends Place>(
  places: TPlace[],
  readAt?: NotificationDateValue,
  limit = DEFAULT_NOTIFICATION_LIMIT,
): PlaceNotification<TPlace>[] => {
  const readAtMillis = toTimestampMillis(readAt);
  const resultLimit = Number.isFinite(limit)
    ? Math.max(0, Math.floor(limit))
    : DEFAULT_NOTIFICATION_LIMIT;

  return places
    .map((place, index) => ({
      index,
      place,
      createdAtMillis: toTimestampMillis(place.createdAt),
    }))
    .sort((first, second) => {
      if (first.createdAtMillis === null) {
        return second.createdAtMillis === null ? first.index - second.index : 1;
      }

      if (second.createdAtMillis === null) {
        return -1;
      }

      return second.createdAtMillis - first.createdAtMillis || first.index - second.index;
    })
    .slice(0, resultLimit)
    .map(({ place, createdAtMillis }) => ({
      place,
      unread: createdAtMillis !== null
        && (readAtMillis === null || createdAtMillis > readAtMillis),
    }));
};
